/**
 * ============================================================================
 * CommonDataContext (client)
 * ============================================================================
 * v5 common data fetched once in the [device]/[country] root layout and
 * shared with client components (Tickybar, StickyWidget, SideButtons, header logo), so pages no longer
 * fetch / prop-drill it. Server components that need other common fields
 * (seatsFilledUI, blogBanner) keep calling fetchCommonData() — React cache()
 * dedupes that with the layout call in the same request.
 * ============================================================================
 */
"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { TickyBarEntryRaw } from "@/components/molecules/TickyBar/model/tickyBar.types";
import type { SideButtonV5Raw } from "@/components/atoms/SideButtons/model/sideButtons.types";
import type {
  ModalThirtySecondImage,
  StickyWidgetData,
} from "@/components/atoms/StickyWidget/model/stickyWidget.types";
import type { HeaderLogoOption } from "@/features/site-chrome/model/siteChrome.types";

export interface CommonDataContextValue {
  /** Route device segment ("desktop" | "mobile"). */
  device: string;
  /** Tickybar entries (commonData.StripTextUI), filtered per page by pageType. */
  stripTextUI: TickyBarEntryRaw[] | null;
  stickyWidget?: StickyWidgetData;
  modalThirtySecondImage?: ModalThirtySecondImage;
  /** Side tray icons (commonData.Section); null → CMS has none yet (no icons). */
  sideButtons?: SideButtonV5Raw[] | null;
  /** Header logos per page prefix (commonData.Header.items); see useHeaderLogo. */
  headerLogos?: HeaderLogoOption[];
}

const CommonDataContext = createContext<CommonDataContextValue>({
  device: "desktop",
  stripTextUI: null,
});

export function CommonDataProvider({
  value,
  children,
}: {
  value: CommonDataContextValue;
  children: ReactNode;
}) {
  return <CommonDataContext.Provider value={value}>{children}</CommonDataContext.Provider>;
}

export function useCommonData(): CommonDataContextValue {
  return useContext(CommonDataContext);
}
